"use client";

import { useContext, useEffect, useState } from "react";

import { usePathname, useRouter } from "next/navigation";

import { type ENavigationKeys } from "../enums/navigation-keys.enum";
import { type INavigationItemWithChildren, type IRoutes } from "../interface";
import { NavigationContext } from "../navigation.provider";

export const useNavigation = <T extends IRoutes>(config?: Record<string, ENavigationKeys | ENavigationKeys[]>) => {
  const pathname = usePathname();
  const router = useRouter();
  const [result, setResult] = useState<Record<string, INavigationItemWithChildren | INavigationItemWithChildren[]>>({});
  const navigation = useContext(NavigationContext);

  if (!navigation) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }

  useEffect(() => {
    if (config) {
      const newResult = Object.entries(config).reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          acc[key] = navigation.findRoutesByKeys(value);
        } else {
          const route = navigation.findRouteByKey(value);
          if (route) {
            acc[key] = route;
          }
        }
        return acc;
      }, {} as Record<string, INavigationItemWithChildren | INavigationItemWithChildren[]>);

      setResult(newResult);
    }
  }, [pathname, config, navigation]);

  return {
    router,
    pathname,
    navigation,
    routes: result as T,
  };
};
